import React, { useContext, useState, useRef, useEffect } from "react";
import {
  LoadingOverlay,
  Button,
  Paper,
  Text,
  Box,
  Stack,
  Group,
  ThemeIcon,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import {
  IconFaceId,
  IconSunHigh,
  IconChefHatOff,
  IconEyeglass2,
} from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import Lottie from "react-lottie-player";
import FaeVerificationAnimation from "../../../assets/fae-verification.json";
import { DataContext } from "../../../context/DataContext";
import { uploadImageToS3 } from "../../../helpers";
import {
  requestCaptureChallenge,
  processAndUploadWithIntegrity,
  createVisibilityTracker,
  createTouchTracker,
  createMotionTracker,
} from "../../../utils/captureIntegrity";
import FirefoxCameraWarning from "../../../components/FirefoxCameraWarning";
import VeratadCapture from "./capture";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";

const ProcessingScreen = ({ locale }) => {
  const [stage, setStage] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);

  const stages = [
    { title: locale?.processing?.uploading || "Uploading...", subtitle: locale?.processing?.uploadingSubtitle || "Sending your image for verification" },
    { title: locale?.processing?.verifying || "Verification in Progress", subtitle: locale?.processing?.verifyingSubtitle || "Analyzing your image..." },
    { title: locale?.processing?.completing || "Completing Verification", subtitle: locale?.processing?.completingSubtitle || "Almost done..." },
  ];

  useEffect(() => {
    const timer1 = setTimeout(() => setStage(1), 2000);
    const timer2 = setTimeout(() => setStage(2), 4000);
    const timer3 = setTimeout(() => setShowSpinner(true), 6500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        padding: "20px",
      }}
    >
      {showSpinner ? (
        <Box
          style={{
            width: 300,
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <Loader size={60} />
        </Box>
      ) : (
        <Lottie
          animationData={FaeVerificationAnimation}
          loop={false}
          play
          style={{ width: 300, height: 300 }}
        />
      )}
      <Box
        style={{
          minHeight: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          fz={24}
          fw={700}
          align="center"
          mt="md"
          style={{
            transition: "opacity 0.5s ease-in-out",
            animation: "fadeIn 0.5s ease-in-out",
          }}
          key={stage}
        >
          {stages[stage].title}
        </Text>
        <Text
          fz="md"
          c="dimmed"
          align="center"
          mt="xs"
          style={{
            transition: "opacity 0.5s ease-in-out",
            animation: "fadeIn 0.5s ease-in-out",
          }}
          key={`subtitle-${stage}`}
        >
          {stages[stage].subtitle}
        </Text>
      </Box>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Box>
  );
};

export default function VeratadFaceCapture({ onCompletion }) {
  const { data, settings, dispatch, postPayloadMessage } =
    useContext(DataContext);
  const [isCapture, setIsCapture] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReadyForStatusCheck, setIsReadyForStatusCheck] = useState(false);
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );
  const theme = useMantineTheme();
  const locale = useLocale("face-capture");
  const loadingStartTimeRef = useRef(null);
  const challengeRef = useRef(null);
  const sessionTimestampsRef = useRef({ session_start: Date.now() });
  const telemetryRef = useRef(null);

  // Poll backend for verification status after upload + process trigger
  if (data?.edge === true) {
    useEdgeStatusCheck(isReadyForStatusCheck);
  } else {
    useStatusCheck(isReadyForStatusCheck, false, "face-capture/veratad");
  }

  const startCapture = () => {
    if (showFirefoxPrompt) return;
    setLoading(true);
    setIsCapture(true);
    sessionTimestampsRef.current.capture_start = Date.now();

    // Initialize interaction telemetry trackers
    telemetryRef.current = {
      visibility: createVisibilityTracker(),
      touch: createTouchTracker(),
      motion: createMotionTracker(),
    };

    if (settings?.request_id) {
      requestCaptureChallenge(settings.request_id)
        .then((c) => { challengeRef.current = c; })
        .catch((err) => console.warn("[CaptureIntegrity] Challenge request failed:", err));
    }
  };

  const handleReady = (cameraSignals) => {
    setLoading(false);
    // Store camera signals from the active video track for integrity telemetry
    if (cameraSignals && telemetryRef.current) {
      telemetryRef.current.camera = cameraSignals;
    }
  };

  const handleCapture = async (base64Image, frameChain) => {
    loadingStartTimeRef.current = Date.now();
    setIsCapture(false);
    setLoading(true);

    try {
      const response = await fetch(base64Image);
      const blob = await response.blob();
      const imageKey = `${settings.request_id}_dl_selfie.jpeg`;
      sessionTimestampsRef.current.capture_end = Date.now();
      await processAndUploadWithIntegrity(
        imageKey, blob, challengeRef.current, settings.request_id,
        "selfie", frameChain, sessionTimestampsRef.current, settings,
        telemetryRef.current
      );

      // Clean up telemetry trackers
      if (telemetryRef.current) {
        telemetryRef.current.visibility?.destroy?.();
        telemetryRef.current.touch?.destroy?.();
        telemetryRef.current.motion?.destroy?.();
        telemetryRef.current = null;
      }

      // If used as a child component with onCompletion callback, just call it
      if (typeof onCompletion === "function") {
        setLoading(false);
        onCompletion();
        return;
      }

      // Standalone route: trigger backend processing and poll for status
      setLoading(false);
      setIsProcessing(true);

      // Prepare data for backend processing
      data.request_id = settings.request_id;
      data.type = "SDK_SCAN";
      data.country = settings.country;
      if (!("front_ext" in data)) {
        data.front_ext = "jpeg";
        data.back_ext = "jpeg";
      }

      // POST to process endpoint to trigger verification
      const edgeQueueUrl =
        process.env.REACT_APP_EDGE_QUEUE_URL ||
        "https://dcams.app/edge/frontend/api/queue/batch";

      const endpoint =
        data?.edge === true
          ? edgeQueueUrl
          : process.env.REACT_APP_BACKEND_URL +
            "/v3/stable/api/frontend/estimate/process.php";

      const requestBody =
        data?.edge === true ? { ...data, queue: "estimate" } : data;

      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      }).catch((err) => console.error("Process request failed:", err));

      // Start status polling after a delay to let the backend begin processing
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const remainingDelay = Math.max(0, 5000 - elapsed);
      setTimeout(() => {
        setIsReadyForStatusCheck(true);
      }, remainingDelay);

      // Send postMessage event
      if (postPayloadMessage) {
        postPayloadMessage({
          event: "VERIFICATION_INITIATED",
          app: "face-capture/veratad",
        });
      }
    } catch (err) {
      console.error("Face capture upload failed:", err);
      setLoading(false);
      setIsProcessing(false);
    }
  };

  const handleError = (err) => {
    console.error("Face capture error:", err);
    setLoading(false);
    setIsCapture(false);
  };

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  // Processing screen after capture — waiting for backend verification
  if (isProcessing) {
    return <ProcessingScreen locale={locale} />;
  }

  return (
    <>
      {loading && <LoadingOverlay visible />}
      {!isCapture ? (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box>
            <Stack align="center">
              <ThemeIcon
                variant="light"
                c={data?.styles?.primaryColor ?? theme.primaryColor}
                size={85}
                radius="xl"
              >
                <IconFaceId size={70} stroke={1} />
              </ThemeIcon>
              <Text fz={30} fw={700} align="center">
                {locale?.setup?.title || "Face Match to ID Document"}
              </Text>
            </Stack>
            <Text align="center">
              {locale?.setup?.instructions || "Before you get started ensure the following:"}
            </Text>
            <Paper
              withBorder
              m="md"
              p="md"
              style={{
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : "#fafafa",
              }}
            >
              <Stack spacing="xl">
                <Group noWrap>
                  <IconSunHigh size={30} />
                  <Text fz={18}>{locale?.setup?.wellLit || "You are in a well-lit area"}</Text>
                </Group>
                <Group noWrap>
                  <IconChefHatOff size={30} />
                  <Text fz={18}>{locale?.setup?.removeHeadwear || "Remove any headwear"}</Text>
                </Group>
                <Group noWrap>
                  <IconEyeglass2 size={30} />
                  <Text fz={18}>{locale?.setup?.keepGlasses || "Keep your glasses on"}</Text>
                </Group>
              </Stack>
            </Paper>
          </Box>
          <Box>
            <Text fz="sm" c="dimmed" align="center">
              {locale?.setup?.cameraPermission || "We will ask you to allow camera permissions"}
            </Text>
            <Button size="lg" onClick={startCapture} fullWidth>
              {locale?.setup?.getStarted || "Get Started"}
            </Button>
          </Box>
        </Box>
      ) : (
        <VeratadCapture
          onCapture={handleCapture}
          onError={handleError}
          onReady={handleReady}
        />
      )}
    </>
  );
}
